(() => {
  'use strict';
  if (window.EdenRoomCardGallery) {
    window.EdenRoomCardGallery.mount(document);
    return;
  }
  const motion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  const arrow = direction => `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="${direction === 'prev' ? 'm15 5-7 7 7 7' : 'm9 5 7 7-7 7'}"/></svg>`;
  let viewer;
  let galleryId = 0;

  function hydrate(image) {
    if (!image?.dataset.src) return;
    image.loading = 'eager';
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    image.src = image.dataset.src;
    delete image.dataset.src;
    delete image.dataset.srcset;
  }

  // Both tracks retain native touch scrolling. Mouse drag adds the equivalent desktop gesture.
  function bindTrack(track, owner, modal = false) {
    let frame = 0;
    let pointer = null;
    let moved = false;
    let suppressUntil = 0;
    track.addEventListener('scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (track.clientWidth) owner.sync(Math.round(track.scrollLeft / track.clientWidth));
      });
    }, {passive: true});
    track.addEventListener('pointerdown', event => {
      owner.loadAround(owner.index);
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      pointer = {id: event.pointerId, x: event.clientX, y: event.clientY, left: track.scrollLeft};
      moved = false;
    }, {passive: true});
    track.addEventListener('pointermove', event => {
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.x;
      if (!moved && Math.abs(dx) > 7 && Math.abs(dx) > Math.abs(event.clientY - pointer.y)) {
        moved = true;
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
      }
      if (!moved) return;
      event.preventDefault();
      track.scrollLeft = pointer.left - dx;
    });
    function finish(event) {
      if (!pointer || pointer.id !== event.pointerId) return;
      pointer = null;
      if (moved) {
        suppressUntil = performance.now() + 400;
        track.classList.remove('is-dragging');
        owner.go(Math.round(track.scrollLeft / (track.clientWidth || 1)));
      }
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      moved = false;
    }
    track.addEventListener('pointerup', finish);
    track.addEventListener('pointercancel', finish);
    track.addEventListener('click', event => {
      if (performance.now() < suppressUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
    track.addEventListener('dragstart', event => event.preventDefault());
    // Prevent the homepage room carousel from also dragging when a photo is dragged.
    track.addEventListener('mousedown', event => event.stopPropagation());
    if (modal) {
      let lastWheel = 0;
      let distance = 0;
      track.addEventListener('wheel', event => {
        if (event.ctrlKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
        event.preventDefault();
        distance += event.deltaY;
        if (Math.abs(distance) < 30 || performance.now() - lastWheel < 240) return;
        owner.go(owner.index + Math.sign(distance));
        lastWheel = performance.now();
        distance = 0;
      }, {passive: false});
    }
    // ResizeObserver supplies the width after layout, including when a hidden
    // hotel tab becomes visible. Mounting every gallery needs no geometry read.
    let width = null;
    const resize = entries => {
      const next = entries?.[0] ? Math.round(entries[0].contentRect.width) : track.clientWidth;
      if (!next || next === width) return;
      const measured = width !== null;
      width = next;
      if (measured || owner.index !== 0) owner.go(owner.index, 'auto');
    };
    if (window.ResizeObserver) new ResizeObserver(resize).observe(track);
    else window.addEventListener('resize', resize, {passive: true});
  }

  function bindKeys(root, owner) {
    root.addEventListener('keydown', event => {
      let next;
      if (event.key === 'ArrowLeft') next = owner.index - 1;
      else if (event.key === 'ArrowRight') next = owner.index + 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = owner.slides.length - 1;
      if (next === undefined) return;
      event.preventDefault();
      event.stopPropagation();
      owner.go(next);
      if (event.target.closest('[data-gallery-dot]')) root.querySelector(`[data-gallery-dot="${owner.index}"]`)?.focus({preventScroll: true});
    });
  }

  function makeViewer() {
    const dialog = document.createElement('dialog');
    dialog.className = 'eg-card-lightbox';
    dialog.setAttribute('aria-labelledby', 'eg-card-lightbox-title');
    dialog.setAttribute('data-eg-room-card-lightbox', '');
    dialog.innerHTML = window.EdenI18n.h("text_2336aa539cec",[arrow('prev'),arrow('next')]);
    document.body.append(dialog);
    const track = dialog.querySelector('.eg-card-lightbox__track');
    const count = dialog.querySelector('.eg-card-lightbox__count');
    const state = {
      dialog, track, slides: [], index: 0, source: null,
      loadAround(index) {
        for (let i = Math.max(0, index - 1); i <= Math.min(this.slides.length - 1, index + 1); i++) hydrate(this.slides[i].querySelector('img'));
      },
      sync(index) {
        this.index = Math.max(0, Math.min(this.slides.length - 1, index));
        count.textContent = `${this.index + 1} / ${this.slides.length}`;
        this.loadAround(this.index);
      },
      go(index, behavior = motion()) {
        if (!this.slides.length) return;
        this.index = (index + this.slides.length) % this.slides.length;
        this.loadAround(this.index);
        track.scrollTo({left: this.index * track.clientWidth, behavior});
        this.sync(this.index);
      },
      open(source) {
        this.source = source;
        dialog.querySelector('.eg-card-lightbox__title').textContent = source.root.dataset.galleryTitle;
        track.replaceChildren();
        this.slides = source.slides.map(slide => {
          const figure = document.createElement('figure');
          figure.className = 'eg-card-lightbox__slide';
          const image = document.createElement('img');
          image.dataset.src = slide.href;
          image.alt = slide.querySelector('img')?.alt || source.root.dataset.galleryTitle;
          image.decoding = 'async';
          figure.append(image);
          track.append(figure);
          return figure;
        });
        dialog.querySelectorAll('.eg-card-lightbox__prev,.eg-card-lightbox__next').forEach(button => {button.hidden = this.slides.length < 2;});
        this.index = source.index;
        document.documentElement.classList.add('eg-room-gallery-open');
        dialog.showModal();
        this.go(source.index, 'auto');
      },
    };
    dialog.querySelector('.eg-card-lightbox__close').addEventListener('click', () => dialog.close());
    dialog.querySelector('.eg-card-lightbox__prev').addEventListener('click', () => state.go(state.index - 1));
    dialog.querySelector('.eg-card-lightbox__next').addEventListener('click', () => state.go(state.index + 1));
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('eg-room-gallery-open');
      state.source?.go(state.index, 'auto');
      state.source?.slides[state.index]?.focus({preventScroll: true});
      track.replaceChildren();
      state.slides = [];
    });
    bindTrack(track, state, true);
    bindKeys(dialog, state);
    return state;
  }

  function init(root) {
    if (root.edenCardGallery) return;
    const track = root.querySelector('[data-gallery-track]');
    const slides = [...root.querySelectorAll('[data-gallery-slide]')];
    if (!track || !slides.length) return;
    const dotGroup = root.querySelector('[data-gallery-dots]');
    const dots = [...root.querySelectorAll('[data-gallery-dot]')];
    track.id ||= 'eg-card-gallery-track-' + (++galleryId);
    const state = {
      root, track, slides, index: 0,
      loadAround(index) {
        for (let i = Math.max(0, index - 1); i <= Math.min(slides.length - 1, index + 1); i++) hydrate(slides[i].querySelector('img'));
      },
      sync(index) {
        this.index = Math.max(0, Math.min(slides.length - 1, index));
        slides.forEach((slide, i) => {slide.tabIndex = i === this.index ? 0 : -1;});
        dots.forEach((dot, i) => {
          if (i === this.index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
          dot.tabIndex = i === this.index ? 0 : -1;
        });
        this.loadAround(this.index);
      },
      go(index, behavior = motion()) {
        this.index = (index + slides.length) % slides.length;
        this.loadAround(this.index);
        track.scrollTo({left: this.index * track.clientWidth, behavior});
        this.sync(this.index);
      },
      open() {
        viewer ||= makeViewer();
        viewer.open(this);
      },
    };
    root.edenCardGallery = state;
    bindTrack(track, state);
    bindKeys(root, state);
    dots.forEach((dot, i) => {
      dot.setAttribute('aria-controls', track.id);
      dot.addEventListener('click', () => state.go(i));
    });
    if (dotGroup) dotGroup.hidden = slides.length < 2;
    track.addEventListener('click', event => {
      const slide = event.target.closest('[data-gallery-slide]');
      if (!slide || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      state.index = slides.indexOf(slide);
      state.open();
    });
    root.addEventListener('pointerenter', () => state.loadAround(state.index), {passive: true});
    if (window.IntersectionObserver) {
      const observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        state.loadAround(state.index);
        observer.disconnect();
      }, {rootMargin: '100px'});
      observer.observe(root);
    }
  }
  function mount(scope) {
    scope.querySelectorAll('[data-room-card-gallery]').forEach(init);
  }
  window.EdenRoomCardGallery = {mount};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), {once: true});
  else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
})();
